/**
 * El cascarón del modal de pago — la anatomía de Stripe Checkout, para pagar sin salir de la venta.
 *
 * ── POR QUÉ ESTA FORMA ────────────────────────────────────────────────────────────────────────
 * El founder pidió «el modal de pago de las grandes empresas: OpenAI, Stripe». Los dos son el mismo
 * patrón (OpenAI monta Stripe Elements): la información se ordena por PREGUNTA, no por formulario.
 *   · Izquierda: quién cobra, cuánto y por qué (logo, «Pagar a …», el monto grande, las líneas,
 *     subtotal y total, y el pie de confianza — el «Powered by Stripe»).
 *   · Derecha: el método como fichas con ícono, sus datos con campos de bordes compartidos (la
 *     «Card information»), y UN solo botón grande que dice lo que va a pasar. Al terminar, el
 *     estado final con el check, en el mismo lugar.
 *
 * ⭐ ES UN CASCARÓN: no sabe de saldo, Yape ni almacenes. `PagarAlAlmacenModal` y `RecargarSaldoModal`
 *    le dan el resumen y el contenido; si mañana hay tarjeta, es una ficha más a la derecha.
 *
 * 🪤 EL MODAL DE POLARIS PONE LA VENTANA (foco, cierre con Escape, scroll, móvil); adentro el
 *    estilo es propio porque Polaris no tiene esta composición a dos paneles. `titleHidden` y sin
 *    `Section`: los paneles van de borde a borde, como en Stripe.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { AppModal } from '@/components/admin/AppModal';
import estilos from './ModalDePago.module.css';

export const CURRENCY = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });

export const ICONO = {
  saldo: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18M16 15h2" /></svg>,
  yape: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18h2" /></svg>,
  plin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M9.5 9.5h5M9.5 13h3" /></svg>,
  transfer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 10l9-5 9 5M5 10v8M9 10v8M15 10v8M19 10v8M3 19h18" /></svg>,
  candado: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>,
  check: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>,
};

export interface LineaDelResumen { nombre: string; detalle?: string; importe: number; imagen?: string | null }
export interface SumaDelResumen { etiqueta: string; valor: string; total?: boolean }

export function ModalDePago({ open, onClose, titulo, comercio, inicial, colorDelLogo = '#1a1f36', pagarA, monto, items, sumas, izquierdaExtra, children }: {
  open: boolean;
  onClose: () => void;
  /** Para el lector de pantalla: el título de la ventana (visualmente lo lleva el panel derecho). */
  titulo: string;
  comercio: string;
  inicial: string;
  colorDelLogo?: string;
  pagarA: string;
  monto: number;
  items?: LineaDelResumen[];
  sumas?: SumaDelResumen[];
  /** Algo más para el panel izquierdo (p. ej. los montos de una recarga). */
  izquierdaExtra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <AppModal open={open} onClose={onClose} title={titulo} titleHidden size="large">
      {/* `data-modal-de-pago`: polaris.css acota la ventana a 840px (el «large» de Polaris son 980). El cierre es el de Polaris. */}
      <div className={estilos.pay} data-modal-de-pago>
        <div className={estilos.resumen}>
          <div className={estilos.comercio}><span className={estilos.logo} style={{ background: colorDelLogo }}>{inicial}</span><span>{comercio}</span></div>
          <div>
            <p className={estilos.pagarA}>{pagarA}</p>
            <p className={estilos.montoGrande}>{CURRENCY.format(monto)}</p>
          </div>
          {items && items.length > 0 ? (
            <div className={estilos.items}>
              {items.map((it, i) => (
                <div key={i} className={estilos.item}>
                  {it.imagen ? <img className={estilos.img} src={it.imagen} alt="" /> : <div className={estilos.img} style={it.imagen === null ? { background: 'transparent' } : undefined} />}
                  <div><b>{it.nombre}</b>{it.detalle ? <span>{it.detalle}</span> : null}</div>
                  <span className={estilos.precio}>{CURRENCY.format(it.importe)}</span>
                </div>
              ))}
            </div>
          ) : null}
          {izquierdaExtra}
          {sumas && sumas.length > 0 ? (
            <div className={estilos.sumas}>
              {sumas.map((s, i) => <div key={i} className={s.total ? estilos.total : undefined}><span>{s.etiqueta}</span><span>{s.valor}</span></div>)}
            </div>
          ) : null}
          <div className={estilos.pie}>{ICONO.candado} Pago protegido por Smarty<span className={estilos.sep} /><span>Términos</span><span>Privacidad</span></div>
        </div>
        <div className={estilos.formulario}>{children}</div>
      </div>
    </AppModal>
  );
}

// ── Las piezas del panel derecho ───────────────────────────────────────────────────────────────

export function TituloDelPago({ children }: { children: ReactNode }) {
  return <h2 className={estilos.titulo}>{children}</h2>;
}

export interface FichaDeMetodo<K extends string> { value: K; label: string; detalle?: string; icono: ReactNode }

export function FichasDeMetodo<K extends string>({ fichas, valor, onCambiar }: { fichas: FichaDeMetodo<K>[]; valor: K; onCambiar: (v: K) => void }) {
  return (
    <div>
      <p className={estilos.etiqueta}>Método de pago</p>
      <div className={estilos.fichas} role="group" aria-label="Método de pago">
        {fichas.map((f) => (
          <button key={f.value} type="button" className={estilos.ficha} aria-pressed={valor === f.value} onClick={() => onCambiar(f.value)}>
            {f.icono}<span>{f.label}</span>{f.detalle ? <small>{f.detalle}</small> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

/** El destino del pago como una «tarjeta guardada»: marca, número grande, titular y Copiar. */
export function DestinoDelPago({ marca, color, titulo, numero, titular }: { marca: string; color: string; titulo: string; numero: string; titular: string }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = useCallback(() => {
    void navigator.clipboard?.writeText(numero.replace(/\s/g, '')).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 1500); });
  }, [numero]);
  return (
    <div className={estilos.destino}>
      <div className={estilos.marca} style={{ background: color }}>{marca}</div>
      <div><span>{titulo}</span><b>{numero}</b><span>{titular}</span></div>
      <button type="button" className={estilos.copiar} onClick={copiar}>{copiado ? 'Copiado' : 'Copiar'}</button>
    </div>
  );
}

/** Monto + código de operación, con bordes compartidos (la «Card information» de Stripe). */
export function CamposDelComprobante({ monto, onMonto, referencia, onReferencia, ayuda }: {
  monto: string; onMonto: (v: string) => void; referencia: string; onReferencia: (v: string) => void; ayuda?: string;
}) {
  return (
    <div className={estilos.campo}>
      <label htmlFor="pago-monto">Comprobante del pago</label>
      <div className={estilos.grupo}>
        <div className={estilos.conIcono}>
          <input id="pago-monto" className={estilos.entrada} inputMode="decimal" value={monto} onChange={(e) => onMonto(e.target.value)} aria-label="Monto pagado" />
          <span className={estilos.ico}>PEN</span>
        </div>
        <div className={estilos.fila}>
          <input className={estilos.entrada} placeholder="Código de operación" value={referencia} onChange={(e) => onReferencia(e.target.value)} aria-label="Código de operación" />
          <input className={estilos.entrada} value={new Intl.DateTimeFormat('es-PE', { day: '2-digit', month: 'short' }).format(new Date())} readOnly aria-label="Fecha" />
        </div>
      </div>
      {ayuda ? <span className={estilos.legal} style={{ textAlign: 'left' }}>{ayuda}</span> : null}
    </div>
  );
}

export function BotonDePagar({ children, onClick, disabled, loading }: { children: ReactNode; onClick: () => void; disabled?: boolean; loading?: boolean }) {
  return (
    <button type="button" className={estilos.boton} aria-disabled={disabled || loading || undefined} onClick={() => { if (!disabled && !loading) onClick(); }}>
      {loading ? 'Un momento…' : children}
    </button>
  );
}

export function Legal({ children }: { children: ReactNode }) {
  return <p className={estilos.legal}>{children}</p>;
}

export function CajaDeSaldo({ saldo, pago }: { saldo: number; pago: number }) {
  return (
    <div className={estilos.saldoBox}>
      <div><span>Tu saldo</span><span>{CURRENCY.format(saldo)}</span></div>
      <div><span>Este pago</span><span>− {CURRENCY.format(pago)}</span></div>
      <div className={estilos.queda}><span>Te queda</span><span>{CURRENCY.format(saldo - pago)}</span></div>
    </div>
  );
}

export function Aviso({ children }: { children: ReactNode }) {
  return <div className={estilos.aviso}>{children}</div>;
}

export function MontosSugeridos({ opciones, valor, onElegir }: { opciones: { valor: number; para: string }[]; valor: number; onElegir: (v: number) => void }) {
  return (
    <div className={estilos.montos} role="group" aria-label="Monto">
      {opciones.map((o) => (
        <button key={o.valor} type="button" className={estilos.monto} aria-pressed={valor === o.valor} onClick={() => onElegir(o.valor)}>
          <b>{CURRENCY.format(o.valor)}</b><span>{o.para}</span>
        </button>
      ))}
    </div>
  );
}

/** El estado final, en el mismo panel: el check y qué pasa ahora. */
export function PasoListo({ titulo, texto, onVolver }: { titulo: string; texto: string; onVolver: () => void }) {
  return (
    <div className={estilos.listo}>
      <div className={estilos.check}>{ICONO.check}</div>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{titulo}</h3>
      <p>{texto}</p>
      <button type="button" className={estilos.boton} style={{ maxWidth: 220 }} onClick={onVolver}>Volver a la venta</button>
    </div>
  );
}
